import {
  EmpresaOperacional,
  EmpresaPapel,
  EmpresaPayload,
  EmpresaStatus,
  EmpresaTipo,
  TecnologiaTratamento,
} from '../../types/empresa';

export const empresaTipoOptions: Array<{ value: EmpresaTipo; label: string }> = [
  { value: 'cliente_contratante', label: 'Cliente contratante' },
  { value: 'parte_interessada', label: 'Parte interessada' },
  { value: 'fornecedor', label: 'Fornecedor' },
  { value: 'canal_comercial', label: 'Canal comercial' },
  { value: 'orgao_referencia', label: 'Órgão de referência' },
  { value: 'outro', label: 'Outro' },
];

export const empresaStatusOptions: Array<{ value: EmpresaStatus; label: string }> = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

export function empresaStatusOptionsForManagement(
  empresa: EmpresaOperacional | null,
): Array<{ value: EmpresaStatus; label: string }> {
  if (!empresa) return empresaStatusOptions.filter((option) => option.value === 'pendente');
  if (empresa.status === 'ativo' || empresa.status === 'inativo') {
    return empresaStatusOptions.filter(
      (option) => option.value === 'ativo' || option.value === 'inativo',
    );
  }
  return empresaStatusOptions.filter((option) => option.value === empresa.status);
}

export const empresaPapelOptions: Array<{ value: EmpresaPapel; label: string }> = [
  { value: 'cliente_contratante', label: 'Cliente contratante' },
  { value: 'comprador_credito', label: 'Comprador de crédito' },
  { value: 'parte_interessada', label: 'Parte interessada' },
  { value: 'home_care', label: 'Home care' },
  { value: 'gerador', label: 'Gerador' },
  { value: 'coletor', label: 'Coletor' },
  { value: 'transportador', label: 'Transportador' },
  { value: 'armazenador_temporario', label: 'Armazenador temporário' },
  { value: 'tratador', label: 'Tratador' },
  { value: 'destinador', label: 'Destinador' },
  { value: 'canal_comercial', label: 'Canal comercial' },
  { value: 'sindicato', label: 'Sindicato' },
  { value: 'revendedor', label: 'Revendedor' },
  { value: 'orgao_referencia', label: 'Órgão de referência' },
];

export const tecnologiaOptions: Array<{ value: TecnologiaTratamento; label: string }> = [
  { value: 'autoclave', label: 'Autoclave' },
  { value: 'micro_ondas', label: 'Micro-ondas' },
  { value: 'tratamento_quimico', label: 'Tratamento químico' },
  { value: 'tratamento_termico', label: 'Tratamento térmico' },
  { value: 'incineracao', label: 'Incineração' },
  { value: 'coprocessamento', label: 'Coprocessamento' },
  { value: 'aterro_classe_i', label: 'Aterro classe I' },
  { value: 'aterro_classe_ii', label: 'Aterro classe II' },
  { value: 'logistica_reversa', label: 'Logística reversa' },
  { value: 'descaracterizacao', label: 'Descaracterização' },
  { value: 'outro_tratamento_licenciado', label: 'Outro tratamento licenciado' },
];

export interface EmpresaFormState {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  tipoEmpresa: EmpresaTipo;
  email: string;
  telefone: string;
  endereco: string;
  municipio: string;
  uf: string;
  status: EmpresaStatus;
  papeis: EmpresaPapel[];
  habilitadoGrupoA: boolean;
  habilitadoGrupoB: boolean;
  habilitadoGrupoE: boolean;
  habilitadoAntineoplasico: boolean;
  tecnologiasTratamentoAutorizadas: TecnologiaTratamento[];
  ufsAtuacao: string;
}

export function emptyEmpresaForm(): EmpresaFormState {
  return {
    cnpj: '',
    razaoSocial: '',
    nomeFantasia: '',
    tipoEmpresa: 'cliente_contratante',
    email: '',
    telefone: '',
    endereco: '',
    municipio: '',
    uf: '',
    status: 'pendente',
    papeis: ['cliente_contratante'],
    habilitadoGrupoA: false,
    habilitadoGrupoB: false,
    habilitadoGrupoE: false,
    habilitadoAntineoplasico: false,
    tecnologiasTratamentoAutorizadas: [],
    ufsAtuacao: '',
  };
}

export function empresaToForm(empresa: EmpresaOperacional): EmpresaFormState {
  return {
    cnpj: formatCnpj(empresa.cnpj),
    razaoSocial: empresa.razaoSocial,
    nomeFantasia: empresa.nomeFantasia ?? '',
    tipoEmpresa: empresa.tipoEmpresa ?? 'outro',
    email: empresa.email ?? '',
    telefone: empresa.telefone ?? '',
    endereco: empresa.endereco ?? '',
    municipio: empresa.municipio ?? '',
    uf: empresa.uf ?? '',
    status: empresa.status,
    papeis: empresa.papeis.filter((item) => item.status === 'ativo').map((item) => item.papel),
    habilitadoGrupoA: empresa.habilitadoGrupoA,
    habilitadoGrupoB: empresa.habilitadoGrupoB,
    habilitadoGrupoE: empresa.habilitadoGrupoE,
    habilitadoAntineoplasico: empresa.habilitadoAntineoplasico,
    tecnologiasTratamentoAutorizadas: empresa.tecnologiasTratamentoAutorizadas,
    ufsAtuacao: empresa.ufsAtuacao.join(', '),
  };
}

export function normalizeCnpj(value: string): string {
  return value.replace(/\D/g, '').slice(0, 14);
}

export function formatCnpj(value: string): string {
  const digits = normalizeCnpj(value);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

export function isValidCnpj(value: string): boolean {
  const digits = normalizeCnpj(value);
  if (digits.length !== 14 || /^(\d)\1+$/.test(digits)) {
    return false;
  }

  const calculateDigit = (length: number): number => {
    let factor = length - 7;
    let sum = 0;
    for (let index = 0; index < length; index += 1) {
      sum += Number(digits[index]) * factor;
      factor -= 1;
      if (factor === 1) factor = 9;
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  return calculateDigit(12) === Number(digits[12]) && calculateDigit(13) === Number(digits[13]);
}

export function parseUfs(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[;,\s]+/)
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean)
    )
  );
}

function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

export function empresaFormToPayload(form: EmpresaFormState): EmpresaPayload {
  const nomeFantasia = optional(form.nomeFantasia);
  const email = optional(form.email);
  const telefone = optional(form.telefone);
  const endereco = optional(form.endereco);
  const municipio = optional(form.municipio);
  const uf = optional(form.uf.toUpperCase());

  return {
    cnpj: normalizeCnpj(form.cnpj),
    razaoSocial: form.razaoSocial.trim(),
    ...(nomeFantasia ? { nomeFantasia } : {}),
    tipoEmpresa: form.tipoEmpresa,
    ...(email ? { email } : {}),
    ...(telefone ? { telefone } : {}),
    ...(endereco ? { endereco } : {}),
    ...(municipio ? { municipio } : {}),
    ...(uf ? { uf } : {}),
    status: form.status,
    papeis: form.papeis,
    habilitadoGrupoA: form.habilitadoGrupoA,
    habilitadoGrupoB: form.habilitadoGrupoB,
    habilitadoGrupoE: form.habilitadoGrupoE,
    habilitadoAntineoplasico: form.habilitadoAntineoplasico,
    tecnologiasTratamentoAutorizadas: form.tecnologiasTratamentoAutorizadas,
    ufsAtuacao: parseUfs(form.ufsAtuacao),
  };
}

export function validateEmpresaForm(form: EmpresaFormState): string[] {
  const errors: string[] = [];
  if (!isValidCnpj(form.cnpj)) errors.push('Informe um CNPJ válido.');
  if (form.razaoSocial.trim().length < 2) errors.push('Informe a razão social.');
  if (form.papeis.length === 0) errors.push('Selecione ao menos um papel operacional.');
  if (form.uf && form.uf.trim().length !== 2) errors.push('A UF deve possuir duas letras.');
  const invalidUf = parseUfs(form.ufsAtuacao).find((uf) => uf.length !== 2);
  if (invalidUf) errors.push(`UF de atuação inválida: ${invalidUf}.`);
  return errors;
}
